import { ref, watch, toRaw } from "vue";
import { defineStore } from "pinia";
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";
import tables from "@/core/worker/tables.json"
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { useModalsStore } from "@/stores/modals";
import { useViewSettingsStore } from "@/stores/viewSettings";
import { useRouter, useRoute } from "vue-router";
import WorkerService from "@/core/services/WorkerService";
import DBConfigService from "@/core/services/DBConfigService";
import { bootDiag } from "@/core/helpers/bootDiag";
import { isAttendeeStatus } from "@/core/helpers/bookingStatus";
import { DEFAULT_TIMEZONE, isValidTimezone, czMakeDate, czParts, czFormat } from "@/core/helpers/companyTime";
import type { DashboardQueryFunction } from "@/core/worker/types/WorkerMessages";

import { databaseWorker as databaseworker, serverWorker as serverworker } from '@/core/worker/workerInstances'

// Guard flags to prevent duplicate triggers from multiple table load completions
let getcacheTriggered = false
let settokenSent = false
let initialWorkerDataRequested = false
let watchersInitialized = false
// True once the boot restore (auth.bootstrapActiveCompany) has run. Until then
// nothing may auto-pick a company — the persisted last active one wins.
let activeCompanyBootstrapped = false
let aclRefreshTimer: ReturnType<typeof setTimeout> | null = null
// Safety net for the manually triggered Full Sync: the overlay is dismissed by
// the worker's 'done'/'syncerror' message, so a worker that never answers at all
// would leave it covering the app forever.
let fullSyncWatchdog: ReturnType<typeof setTimeout> | null = null
// Offene Promises von socketEmitWithAck(), Schlüssel ist die callbackId.
const ackCallbacks = new Map<string, (result: any) => void>()
// Offene Promises von socketProxyConfirmed(), Schlüssel ist `<tabelle>:<id>`.
// Aufgelöst wird eine Wartung ausschliesslich vom pushProxy-Echo des Servers —
// das kommt erst NACH einem erfolgreichen INSERT/UPDATE, ist also der einzige
// Beleg, dass die Zeile wirklich in der Datenbank steht.
const writeConfirmations = new Map<string, {
  resolve: () => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}>()
function settleWriteConfirmation(table: string, id: string, err?: Error): void {
  if (!table || !id) return
  const pending = writeConfirmations.get(`${table}:${id}`)
  if (!pending) return
  writeConfirmations.delete(`${table}:${id}`)
  clearTimeout(pending.timer)
  err ? pending.reject(err) : pending.resolve()
}
// Sammelpuffer von socketProxyBatch() samt seinem Debounce-Timer.
let proxyBatchBuffer: Array<{ cmd: string; table: string; data: any }> = []
let proxyBatchTimer: ReturnType<typeof setTimeout> | null = null

export const useTablesStore = defineStore('tables', {
  // arrow function recommended for full type inference
  state: () => {
    return {
      initialimportfromserver: false,
      // True while the user-triggered Full Sync runs — makes the fullscreen
      // loading overlay (modals/loading/initialize.vue) visible even though
      // the store still holds the old rows.
      fullsyncrunning: false,
      initalworkerdatarequestdone: false,
      updatefromserverarrived: false,
      isLoggedin: false,
      config: {
        reportsyear: new Date().getFullYear(),
        waitingforresponse: false,
        passwordwrong: false,
        authretries: 0,
        appversion: '1.000.240',
        appversionnumber: 1000240,
        databaseversion: '2',
        databaseversionnumber: 2,
        refreshdatabase: false,
        isConnected: false,
        isAuthenticated: false,
        isLoggedin: false,
        isLoading: false,
        CacheisLoading: false,
        MobilOptions: false,
        eventsloaded: false,
        bookingsloaded: false,
        isOnline: 'online',
        hasLoaded: false,
        rightsloaded: false,
        loggedoutbyuser: true,
        token: '',
        lasturlpath: '',
        registerpossible: false,
        initalserverdatarequestdone: false,
        initalworkerdatarequestdone: false,
        initalscriptrequestdone: false,
        deviceid: '',
        CalenderViewMode: 'studio',
        ResolutionMode: '',
        serverconnectionlostdate: '',
        ActiveCompany: '',
        ActiveLocation: '',
        ActiveRoom: '',
        ActiveTimezone: 'Europe/Berlin',
        ActiveClient: '',
        // Zuletzt offener Tab in ClientDetail fuer den Kunden im Kundenmodus —
        // die Links „Zum Kundenprofil" in Header und Sidebar tragen keinen Hash.
        ActiveClientTab: { clientId: '', tab: '' },
        ActiveUserCompanyACLRights: [],
        ActiveUserCompanyACLLevel: [],
        // String, kein Array: ein leeres Array ist truthy und gewinnt damit
        // jede `x || fallback`-Kette. Genau so gingen am 2026-08-28 auf prod
        // 25 Kalendersätze verloren (createdbyid = [] -> ER_PARSE_ERROR).
        ActiveUserCompanyResource: '',
        ActiveExerciseId: '',
        ActiveWorkoutId: '',
        ActiveProgramId: '',
        workoutoverviewactivetab: 0,
        month_cache: {
          start: '',
          end: '',
          year: '',
          // 0-basierte Monatszahl wie getMonth(), -1 solange nichts geladen ist.
          month: -1,
          companies: '',
          initialcacheupdatedone: false
        },
        lastaddedclient: {},
        lastaddedbooking: {},
        hoverservice: '',
        AppVersion: '',
        tablesarrived: false,
        // tables.json liefert `loaded: "false"` als String, der Code setzt
        // danach echte Booleans. Beides ist im Umlauf, und die Leser prüfen
        // entsprechend `!table.loaded || table.loaded === 'false'`. Der Typ
        // bildet diesen Ist-Zustand ab, statt eine der beiden Formen zu
        // behaupten, die es so nicht gibt.
        tables: tables.tables as Array<{
          name: string
          description: string
          index: string
          loaded: boolean | string
        }>,
      },
      dateconfig: {
        ActiveDate: new Date("2025-11-01").formatDate("YYYY-MM-DD"),
        ActiveWeek: new Date("2025-11-01").getWeekNumber(),
        ActiveMonth: new Date("2025-11-01").getMonth(),
        ActiveYear: new Date("2025-11-01").getFullYear(),
        DateTimer: [],
        StartOfWeek: 1,
        // Beschriftungsraster der Zeitspalte (Tag/Woche/Monat): Halbstundentakt
        ShowTimeStepsInMinutes: 30,
        ShowResources: false,
        ShowResourcesOverview: false,
        showtodos: false,
        showarchivedtrainings: false,
        ViewedTimeStepsInMinutes: 15,
        cellheight: 40,
        daystoshow: 7,
        viewdays: [
          { value: 1, day: 'Montag' },
          { value: 2, day: 'Dienstag' },
          { value: 3, day: 'Mittwoch' },
          { value: 4, day: 'Donnerstag' },
          { value: 5, day: 'Freitag' },
          { value: 6, day: 'Samstag' },
          { value: 0, day: 'Sonntag' }
        ],
        cellwidth: '',
        sizechanged: '',
        day: {
          cellheight: '',
          cellwidth: ''
        },
        week: {
          cellheight: '',
          cellwidth: ''
        },
        month: {
          cellheight: '',
          cellwidth: ''
        },
        CalendarView: 'week',  // Default to Planning + Week view
        ActiveTimezone: 0,
        ActiveRoom: { "Alle Räume": "Alle Räume" },
        ActiveEmployees: { "Alle Mitarbeiter": "Alle Mitarbeiter" },
        ActiveActivities: { "Alle Aktivitäten": "Alle Aktivitäten" },
        ActiveActivityTypes: { "All Kurstypen": "Alle Kurstypen" },
        cachingevent: {
          month: '',
          start: '',
          end: ''
        }
      },
      pwa: {
        indexedDB: false,
        serviceworkeravailable: false,
        serviceworkerinstalled: false,
        serviceworkerready: false,
        systemreadytoaddapptohome: false,
        appisalreadyinstalledonhome: false,
        appinstalledtohome: false,
        applaunchedfromhomescreen: false,
        databaseworkerready: false,
        dbloading: {},
        filestocache: [],
        pdfreceived: false,
        pdfcache: []
      },
      dashboardQueries: {
        loading: new Map<string, boolean>(),
        results: new Map<string, any>(),
        errors: new Map<string, string>()
      },
      calendarCache: {
        status: 'idle' as 'idle' | 'loading' | 'loaded' | 'error',
        lastUpdate: null as Date | null,
        error: null as string | null,
        currentWindow: {
          start: '',
          end: '',
          month: -1,
          year: -1
        }
      },
      // Legacy custom request system for dashboard widgets
      customrequests: [] as any[],
      completedqueue: [] as any[],
      // Anzeige im Sync-Dropdown des Kopfbereichs. Die vier Zustände bedeuten
      // Verschiedenes und dürfen nicht vermischt werden:
      //   pending — abgeschickt, Echo des Servers steht aus
      //   done    — der Server hat die Zeile per pushProxy bestätigt
      //   offline — Socket war zu, der Write liegt lokal und in der Outbox und
      //             geht beim Reconnect raus (siehe server.worker.ts)
      //   error   — der Write ist NICHT gespeichert und wird es auch nicht mehr:
      //             der Server hat ihn abgelehnt ('writefailed') oder er wurde
      //             bei geschlossenem Socket mit requireServer verworfen.
      syncQueue: [] as { id: string, cmd: string, table: string, recordId: string, timestamp: Date, status: 'pending' | 'done' | 'offline' | 'error', label: string }[],
      // Drag-and-drop state shared across calendar views (day, week, month, sidebar)
      drag: null as null | {
        daysdropzone?: boolean
        drag?: boolean
        draggedelement?: {
          autoopenmodal: boolean
          dom: HTMLElement | null
          dataobj: any
          shiftY: number
        }
      },
      temp: {
        Calendar_events_month_cache: [] as any[],
        Calendar_bookings_month_cache: [] as any[],
        Calendar_bookings_by_activeclient: [] as any[],
        communication_outbox_internal_emails: [] as any[],
        modaleventcontracts: [] as any[],
        modaleventbookings: [] as any[],
      },
      tables: {
        app_components: [],
        calendar_bookings: [],
        calendar_events: [],
        calendar_series: [],
        clients_info: [],
        clients_infoconfig: [],
        clients_mapping: [],
        clients_mapping_info: [],
        communication_accounts: [],
        communication_accounts_emailaddresses: [],
        communication_accounts_types: [],
        communication_outbox_internal_emails: [],
        companies: [],
        companies_connected: [],
        companies_acl_level: [],
        companies_acl_level_rights: [],
        companies_acl_mapping: [],
        companies_clients_groups: [],
        companies_healthstate: [],
        companies_infos: [],
        companies_pricerules: [],
        companies_rules: [],
        contracts_offered: [],
        contracts_offered_prices: [],
        contracts_offered_rules: [],
        contracts_sales: [],
        contracts_sales_rules: [],
        equipment_items: [],
        equipment_kinds: [],
        finance_sales_invoices_outgoing: [],
        finance_sales_invoices_outgoing_positions: [],
        locations: [],
        locations_openinghours: [],
        locations_rooms: [],
        media_files: [],
        media_folders: [],
        products: [],
        products_categories: [],
        product_prices: [],
        resources: [],
        resources_calendar: [],
        resources_companies: [],
        resources_companies_info: [],
        rules: [],
        services: [],
        services_kindsof: [],
        services_rules: [],
        tasks: [],
        tasks_dashboards: [],
        user: [],
        user_acl_level: [],
        user_acl_mapping: [],
        widgets: [],
        workouts_client_schedules: [],
        workouts_exercises: [],
        workouts_program_templates: [],
        workouts_workout_templates: [],
        companies_integrations: [],
        fitness_programs: [],
        fitness_measurements: [],
        fitness_program_templates: [],
        dashboards: [],
        dashboard_cards: [],
        dashboard_visibility_groups: [],
        // ── Management-synced subscription tables (read-only mirror of mycrogym_management) ──
        mgmt_studio_packages: [],
        mgmt_studio_subscriptions_templates: [],
        mgmt_studio_subscriptions: [],
        mgmt_studio_payment_schedules: [],
        mgmt_legal_documents: [],
        mgmt_consent_audits: [],
        mgmt_languages: [],
        // ── Vorlagen-Katalog (read-only mirror; import runs server-side) ──
        mgmt_template_catalog: [],
        // Frozen release snapshots — studios never mirror live template tables
        mgmt_template_releases: [],
        mgmt_studio_purchases: [],
      } as Record<string, any[]>,
    }
  },
  getters: {
    /**
     * Zeitzone des Studios — die Zeitzone, in der der Kalender angezeigt wird.
     *
     * Quelle ist `companies_infos` mit `type = 'timezone'`; die Tabelle synct
     * ohnehin in die IndexedDB (table-registry.js:91), es braucht dafuer also
     * keinen HTTP-Aufruf. Ohne gepflegten Eintrag gilt Europe/Berlin — das ist
     * der Stand, den alle Bestandsstudios heute faktisch haben.
     *
     * Bewusst NICHT die Browser-Zeitzone als Fallback: dann haette derselbe
     * Termin fuer einen Trainer im Urlaub eine andere Uhrzeit als fuer die
     * Kollegin im Studio, und keiner der beiden saehe, dass er abweicht.
     */
    companyTimezone(state): string {
      const rows = (state.tables as any).companies_infos || []
      const row = rows.find((r: any) =>
        r && r.companyid === state.config.ActiveCompany && r.type === 'timezone' && !r.archive
      )
      if (!row) return DEFAULT_TIMEZONE
      try {
        const opts = typeof row.options === 'string' ? JSON.parse(row.options) : row.options
        const tz = opts?.timezone
        if (isValidTimezone(tz)) return tz
      } catch { /* kaputtes JSON darf den Kalender nicht anhalten */ }
      return DEFAULT_TIMEZONE
    },
    geteventsbycompany(state) {
      const events = state.temp.Calendar_events_month_cache.filter(event => event.companyid === state.config.ActiveCompany)
      // In day view all rooms are displayed as separate columns — room filter must not apply
      const calView = (state as any).dateconfig?.CalendarView
      const isDayView = calView === 'day' || calView === 'compactday'
      if (state.config.ActiveRoom && !isDayView) {
        return events.filter(event => event.roomid === state.config.ActiveRoom)
      }
      return events
    },
    /**
     * Aktive Buchungen des Monats-Caches, indiziert nach calendareventid.
     *
     * Warum als Getter und nicht pro Event-Karte: Calendar_Event.vue existiert in
     * hunderten Instanzen. Filtert jede Karte selbst, laeuft der Filter N-mal pro
     * Cache-Mutation — genau das hat frueher die UI eingefroren und wurde damals mit
     * einem nicht-tiefen Watcher "geloest", der dann push()/splice() im Cache gar
     * nicht mehr bemerkt hat (neue/geloeschte Buchung blieb in der Kalenderansicht
     * unsichtbar). Als Pinia-Getter laeuft der Aufbau genau EINMAL pro Mutation und
     * alle Karten lesen daraus.
     */
    bookingsByEventId(state): Record<string, any[]> {
      const map: Record<string, any[]> = {}
      for (const booking of state.temp.Calendar_bookings_month_cache) {
        if (Number(booking.archive) !== 0) continue
        if (!isAttendeeStatus(booking.status)) continue
        const eventid = booking.calendareventid
        if (!eventid) continue
        if (!map[eventid]) map[eventid] = []
        map[eventid].push(booking)
      }
      return map
    },
    /**
     * Bezahlte, aktive Buchungen je Vertrag — Grundlage jeder Kontingentpruefung
     * im Kundenmodus (Woche, Vertragszeitraum, Karte).
     *
     * Zwei Quellen, bewusst vereinigt: `tables.calendar_bookings` traegt den
     * Bestand ueber den ganzen Zeitraum, der Monats-Cache die gerade ueber den
     * Socket eingetroffenen Zeilen. Ohne den Cache zaehlte eine soeben gebuchte
     * Stunde erst nach dem naechsten Sync auf das Wochenkontingent.
     *
     * Als Getter aus demselben Grund wie bookingsByEventId: die Kalenderkarten
     * existieren hundertfach und wuerden den Index sonst jede fuer sich bauen.
     */
    paidBookingsByContractId(state): Record<string, any[]> {
      const map: Record<string, any[]> = {}
      const seen = new Set<string>()
      const collect = (rows: any[]) => {
        for (const booking of rows || []) {
          if (!booking || !booking.contractid) continue
          if (booking.kindofbooking !== 'paidpractice') continue
          if (Number(booking.archive) !== 0) continue
          if (seen.has(booking.id)) continue
          seen.add(booking.id)
          if (!map[booking.contractid]) map[booking.contractid] = []
          map[booking.contractid].push(booking)
        }
      }
      collect(state.tables.calendar_bookings as any[])
      collect(state.temp.Calendar_bookings_month_cache as any[])
      return map
    },
    isConnected(state) {
      return state.config.isConnected
    }
  },
  actions: {
    storeNew(data) {
      this.tables[data.table].push(data.result)
      if (data.table === 'calendar_events') {
      }
      if (data.table === 'calendar_bookings') {
      }
    },
    storeUpdate(data) {

    },
    bulkPut(data) {

    },
    /**
     * Converts date strings to proper Date objects
     * Handles both legacy format (YYYY-MM-DD HH:mm:ss) and ISO format
     * Worker-safe: doesn't access store or external dependencies
     */
    // Rückgabe schließt null ein: das MySQL-Nulldatum "0000-00-00 00:00:00"
    // wird bewusst zu null aufgelöst (siehe Guard im Rumpf), und alle Aufrufer
    // schreiben das Ergebnis in Felder, die leer sein dürfen.
    convertDateForIos(dateString: string | Date): Date | string | null {
      // If already a Date object, return as-is
      if (dateString instanceof Date) {
        return dateString
      }

      // If not a string, return as-is
      if (typeof dateString !== 'string') {
        return dateString
      }

      // DB format: "YYYY-MM-DD HH:mm:ss" (space-separated, stored as UTC).
      // Data migrated to UTC on 2026-05-18. We use Date.UTC() so the browser
      // treats the value as UTC and automatically converts to local time for
      // display (e.g. 05:00 UTC → 07:00 CEST).
      if (dateString.includes(' ') && dateString.includes('-') && dateString.includes(':')) {
        // Guard: MySQL zero-date "0000-00-00 00:00:00" is an invalid/null date
        if (dateString.startsWith('0000-')) return null

        const parts = dateString.split(/[- :]/)
        if (parts.length >= 6) {
          const year   = parseInt(parts[0])
          const month  = parseInt(parts[1]) - 1 // JS months are 0-indexed
          const day    = parseInt(parts[2])
          const hour   = parseInt(parts[3])
          const minute = parseInt(parts[4])
          const second = parseInt(parts[5])
          // Create Date from UTC components — browser converts to local automatically
          return new Date(Date.UTC(year, month, day, hour, minute, second))
        }
      }

      // For ISO format strings (from real-time push events):
      // The push path sends UTC ISO strings; new Date() converts UTC→local,
      // recovering the original local time.
      try {
        const date = new Date(dateString)
        if (!isNaN(date.getTime())) {
          return date
        }
      } catch (e) {
        console.error('Error parsing date:', dateString, e)
      }

      // If all else fails, return the original string
      return dateString
    },
    changeparameter(parameter, value) {
      this[parameter] = value
    },
    /**
     * Company-Wechsel: Tabellen neu senden lassen UND die Kalender-Caches neu laden.
     *
     * Der Name täuscht: diese Aktion hat nie Kalenderdaten angefordert, sie schickte
     * `getcache` (= alle Tabellen erneut) — setzte aber `CacheisLoading = true`.
     * Gelöscht wird dieses Flag ausschließlich von der `bookingscache`-Antwort, und
     * die entsteht nur aus `getcalendarcaches` (refreshcalendarcaches2). Der
     * getcache-Zyklus antwortet nie darauf: das Flag blieb hängen, die Dashboard-
     * Karten (`v-if="… && !CacheisLoading"`) blieben leer, und der Kalender-Watchdog
     * (Calendar_Manager.vue) fordert nur bei `!CacheisLoading` nach — der Kalender
     * konnte sich also nicht mehr selbst erholen.
     *
     * Aufgefallen ist das erst auf dem iPad: seit der ACL-Koaleszierung im
     * Query-Worker (emitLegacyACLData rerun) trifft `useracldata_batch` ein ZWEITES
     * Mal ein, und dort ist `wasRightsLoaded` dann true → genau dieser Pfad feuert.
     * Auf schnellen Geräten landet der Rerun noch vor `hasLoaded`, dort passiert
     * nichts. Deshalb: die Kalenderanfrage an refreshcalendarcaches2 delegieren,
     * die einzige Stelle mit Antwort UND Sicherheits-Timeout.
     */
    refreshcalendarcaches(newdate) {
      // `companies` stand auch hier als ungelesener zweiter Parameter — die
      // Firma kommt aus this.config.ActiveCompany, siehe die Zeile darunter.
      console.log('refreshcalendarcaches')
      databaseworker.postMessage({ 'cmd': 'getcache', 'query': { companies: this.config.ActiveCompany } })
      this.refreshcalendarcaches2(newdate)
    },
    /**
     * Refresh calendar cache - TEMPORARY using legacy worker
     */
    /**
     * `companies` gab es hier als zweiten Parameter — er wurde im Rumpf NIE
     * gelesen; die Anfrage nimmt die Firma aus `this.config.ActiveCompany`.
     * Sechs der sieben Aufrufer haben ihn ohnehin weggelassen, nur
     * refreshcalendarcaches() reichte etwas durch, das im Nichts endete.
     */
    refreshcalendarcaches2(newdate) {
      console.log('refreshcalendarcaches2')
      console.log(newdate)
      this.config.eventsloaded = false
      this.config.bookingsloaded = false
      this.config.CacheisLoading = true
      const tz = this.companyTimezone
      // ── Fenstergrenzen in Studiozeit, nicht in Geraetezeit ──────────────────
      // Vorher: `new Date(newdate)` auf einem "YYYY-MM-DD"-String. Das ist
      // UTC-Mitternacht; `getMonth()` liest daraus die ORTSZEIT des Geraets.
      // Westlich von Greenwich liefert das bereits den Vormonat — der Cache
      // haette dann ein um einen Monat verschobenes Fenster geladen. Und da
      // die Anzeige jetzt der Studiozeit folgt, muss die Fenstergrenze
      // mitwandern: sonst tauscht man einen Anzeigefehler gegen fehlende
      // Daten am Monatsrand.
      const ref = typeof newdate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(newdate)
        ? { year: Number(newdate.slice(0, 4)), month: Number(newdate.slice(5, 7)), day: Number(newdate.slice(8, 10)) }
        : (czParts(newdate, tz) || czParts(new Date(), tz)!)
      // Ein Monat davor bis drei Monate danach — jeweils 00:00 Studiozeit.
      // czMakeDate normalisiert Monat 0 und 13+ ueber Date.UTC von selbst.
      const firstDay = czMakeDate(ref.year, ref.month - 1, 1, 0, 0, 0, tz)
      const lastDay  = czMakeDate(ref.year, ref.month + 3, 1, 0, 0, 0, tz)
      this.config.month_cache.start = czFormat(firstDay, tz, "YYYY-MM-DD")
      this.config.month_cache.end = czFormat(lastDay, tz, "YYYY-MM-DD")
      this.config.month_cache.month = ref.month - 1   // 0-basiert, wie getMonth()
      this.config.month_cache.year = String(ref.year)
      this.config.month_cache.initialcacheupdatedone = true
      const dataobject = {
        year: ref.year,
        // Echte Zeitpunkte an den Worker — der macht daraus per toISOString()
        // die UTC-Grenzen der indizierten .between()-Abfrage.
        start: firstDay,
        end: lastDay,
        month: ref.month - 1,
        companies: this.config.ActiveCompany
      }
      console.log(dataobject)
      databaseworker.postMessage({ 'cmd': 'getcalendarcaches', 'query': dataobject })
      // Safety timeout: clear CacheisLoading if bookingscache response never arrives
      // (e.g. worker guard stuck or empty DB on first run)
      setTimeout(() => {
        if (this.config.CacheisLoading) {
          console.warn('⚠️ CacheisLoading timeout — forcing clear to unblock dashboard cards')
          this.config.CacheisLoading = false
        }
      }, 10000)
    },
    /**
     * Legacy custom request to database worker
     * Used by dashboard widgets that haven't been fully migrated
     */
    customrequeststodatabase(config: any) {
      console.log('customrequeststodatabase', config)

      // Create a clean, serializable version of the config
      const serializedConfig = JSON.parse(JSON.stringify(config))

      // Send request to legacy worker
      databaseworker.postMessage({ 'cmd': config.function, config: serializedConfig })

      // Track the request
      this.customrequests.push({
        id: config.id,
        function: config.function,
        config: serializedConfig,
        data: []
      })
    },
    /**
     * Delete a custom request from tracking
     */
    customrequestdelete(id: string) {
      const index = this.customrequests.findIndex(r => r.id === id)
      if (index !== -1) {
        this.customrequests.splice(index, 1)
      }

      // Also remove from completed queue
      const completedIndex = this.completedqueue.findIndex(r => r.id === id)
      if (completedIndex !== -1) {
        this.completedqueue.splice(completedIndex, 1)
      }
    },
    setlocationbyactivecompany() {
      if (!this.config.ActiveCompany) return

      const viewSettingsStore = useViewSettingsStore()
      const savedSettings = viewSettingsStore.getCompanySettings(this.config.ActiveCompany)

      // ── Restore location ───────────────────────────────────────────────────
      if (savedSettings.activelocation) {
        this.config.ActiveLocation = savedSettings.activelocation
      } else {
        // No saved location → use first active location for this company
        const locations = this.tables.locations.filter(
          l => l.companyid === this.config.ActiveCompany && l.archive === 0
        )
        if (locations.length > 0) {
          this.config.ActiveLocation = locations[0].id
        }
      }

      // ── Restore room (or auto-select first) ────────────────────────────────
      const locationId = this.config.ActiveLocation
      const roomsForLocation = this.tables.locations_rooms.filter(
        r => r.locationid === locationId && r.archive === 0
      )

      if (savedSettings.activeroom && roomsForLocation.some(r => r.id === savedSettings.activeroom)) {
        // Saved room still exists → restore it
        this.config.ActiveRoom = savedSettings.activeroom
      } else if (roomsForLocation.length > 0) {
        // No saved room (or saved room no longer exists) → auto-select first room
        this.config.ActiveRoom = roomsForLocation[0].id
        // Persist the auto-selected room immediately
        viewSettingsStore.updateActiveSelections(this.config.ActiveCompany, {
          activelocation: locationId,
          activeroom: roomsForLocation[0].id
        })
      } else {
        this.config.ActiveRoom = ''
      }

      // ── Restore active date (fall back to today) ───────────────────────────
      if (savedSettings.calendardate) {
        const restoredDate = new Date(savedSettings.calendardate + 'T12:00:00')
        if (!isNaN(restoredDate.getTime())) {
          this.dateconfig.ActiveDate = savedSettings.calendardate
          this.dateconfig.ActiveWeek = restoredDate.getWeekNumber()
          this.dateconfig.ActiveMonth = restoredDate.getMonth()
          this.dateconfig.ActiveYear = restoredDate.getFullYear()
        }
      } else {
        const today = new Date()
        this.dateconfig.ActiveDate = today.formatDate('YYYY-MM-DD')
        this.dateconfig.ActiveWeek = today.getWeekNumber()
        this.dateconfig.ActiveMonth = today.getMonth()
        this.dateconfig.ActiveYear = today.getFullYear()
      }

      // ── Restore calendar view ──────────────────────────────────────────────
      this.restorecalendarview()
    },
    /**
     * Gespeicherte Kalenderansicht wiederherstellen — der EINZIGE Ort, an dem
     * CalendarView aus den viewSettings gelesen wird.
     *
     * Vorher stand dieselbe if/else-Kette dreimal (hier, Calendar_Manager.created,
     * Toolbar.mounted) und jede Kopie lief zu einem anderen Zeitpunkt im Boot.
     * Der Haken: solange die viewSettings noch nicht aus der IndexedDB geladen
     * sind (oder noch keine Firma feststeht), liefert getCompanySettings() frische
     * DEFAULTS — 'week'/'planning'. Wer die dann setzt, überschreibt eine bereits
     * korrekt wiederhergestellte Tagesansicht, und der activeview-Watcher der
     * Toolbar schreibt das 'week' anschließend auch noch in die IndexedDB zurück:
     * die Tagesansicht war nach einem Refresh dauerhaft verloren.
     *
     * Deshalb: ohne isInitialized + ActiveCompany wird NICHTS gesetzt. Der
     * Aufrufer erkennt das am Rückgabewert und kann später erneut anfragen.
     */
    restorecalendarview(): boolean {
      const viewSettingsStore = useViewSettingsStore()
      if (!this.config.ActiveCompany || !viewSettingsStore.isInitialized) return false

      const savedSettings = viewSettingsStore.getCompanySettings(this.config.ActiveCompany)
      if (savedSettings.calendarviewmode && savedSettings.calendarviewmode !== 'planning') {
        this.dateconfig.CalendarView = savedSettings.calendarviewmode
      } else if (savedSettings.calendarview) {
        this.dateconfig.CalendarView = savedSettings.calendarview
      }
      // Die Kundenliste ergibt nur im Kundenmodus Sinn
      if (this.dateconfig.CalendarView === 'clientlist' && !this.config.ActiveClient) {
        this.dateconfig.CalendarView = 'week'
      }
      return true
    },
    // Navigate to next week
    navigateWeekForward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setDate(currentDate.getDate() + 7)
      this.updateDateConfig(currentDate)
    },
    // Navigate to previous week
    navigateWeekBackward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setDate(currentDate.getDate() - 7)
      this.updateDateConfig(currentDate)
    },
    // Update date configuration and check if month cache needs updating
    updateDateConfig(newDate: Date) {
      const formattedDate = newDate.formatDate("YYYY-MM-DD")

      // ── Early exit if nothing changed ──────────────────────────────────────
      // Prevents the feedback loop: picker.move() → v-model setter → updateDateConfig
      // → updateCalendarSettings → saveToIndexedDB → repeat.
      if (this.dateconfig.ActiveDate === formattedDate) return

      const newMonth = newDate.getMonth()
      const newYear = newDate.getFullYear()
      const currentMonth = this.dateconfig.ActiveMonth
      const currentYear = this.dateconfig.ActiveYear

      // Update dateconfig
      this.dateconfig.ActiveDate = formattedDate
      this.dateconfig.ActiveWeek = newDate.getWeekNumber()
      this.dateconfig.ActiveMonth = newMonth
      this.dateconfig.ActiveYear = newYear

      // Persist active date to viewSettings so it survives page reload
      if (this.config.ActiveCompany) {
        const viewSettingsStore = useViewSettingsStore()
        viewSettingsStore.updateCalendarSettings(this.config.ActiveCompany, {
          calendardate: formattedDate
        })
      }

      // Check if we crossed into a different month
      if (newMonth !== currentMonth || newYear !== currentYear) {
        console.log('Month boundary crossed, updating cache...')
        this.refreshcalendarcaches2(formattedDate)
      }
    },
    // Navigate to next day
    navigateDayForward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setDate(currentDate.getDate() + 1)
      this.updateDateConfig(currentDate)
    },
    // Navigate to previous day
    navigateDayBackward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setDate(currentDate.getDate() - 1)
      this.updateDateConfig(currentDate)
    },
    // Navigate to next month
    navigateMonthForward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setMonth(currentDate.getMonth() + 1, 1)
      this.updateDateConfig(currentDate)
    },
    // Navigate to previous month
    navigateMonthBackward() {
      const currentDate = new Date(this.dateconfig.ActiveDate + 'T12:00:00')
      currentDate.setMonth(currentDate.getMonth() - 1, 1)
      this.updateDateConfig(currentDate)
    },
    /** Called by auth once the boot restore of ActiveCompany has finished.
     *  Releases the server worker's setActiveCompany fallback, which may then
     *  fill in a company only if the restore produced none. */
    markActiveCompanyBootstrapped() {
      activeCompanyBootstrapped = true
    },
    /**
     * Alles verwerfen, was zur angemeldeten Person gehört. Wird von
     * auth.logout() gerufen.
     *
     * Der Logout ist ein reiner Router-Wechsel (kein Page-Reload), die Pinia-
     * Stores überleben ihn also. Ohne diesen Reset behielt der nächste Login
     * die Rechte des vorherigen Mitarbeiters: `rightsloaded` blieb true und
     * `ActiveUserCompanyACLRights`/`-Level` blieben gefüllt, bis die ACL-
     * Ableitung des Query-Workers zurückkam — bis dahin beantwortete
     * usePermissions() jede Prüfung mit der alten ACL (inkl. isAdmin).
     *
     * `rightsloaded = false` ist dabei der entscheidende Teil: die Prüfungen
     * fallen damit in den "noch nicht geladen"-Zweig und verweigern, statt zu
     * raten.
     */
    resetSessionState() {
      const userStore = useUserStore()

      // Modul-Guards: `settokenSent` ist auf "einmal pro Page-Session"
      // ausgelegt — ohne Reset bekäme der Server-Worker nach einem Re-Login
      // im selben Tab nie das Token des neuen Users und synchronisierte
      // weiter mit dem alten.
      settokenSent = false
      getcacheTriggered = false
      initialWorkerDataRequested = false
      activeCompanyBootstrapped = false
      if (aclRefreshTimer) {
        clearTimeout(aclRefreshTimer)
        aclRefreshTimer = null
      }

      this.config.rightsloaded = false
      this.config.hasLoaded = false
      this.config.isLoading = false
      this.config.ActiveUserCompanyACLRights = []
      this.config.ActiveUserCompanyACLLevel = []
      this.config.ActiveUserCompanyResource = ''
      // ActiveCompany gehört zur alten Session — bootstrapActiveCompany()
      // nutzt sie sonst als Fallback #2 und öffnet dem neuen User die zuletzt
      // gewählte Company des vorherigen.
      this.config.ActiveCompany = ''
      this.config.ActiveLocation = ''
      this.config.ActiveRoom = ''
      this.config.ActiveClient = ''
      this.config.ActiveClientTab = { clientId: '', tab: '' }
      this.config.initalworkerdatarequestdone = false
      this.initalworkerdatarequestdone = false

      userStore.setUserData('ActiveUserCompanyACLRights', [])
      userStore.setUserData('ActiveUserCompanyACLLevel', [])
      userStore.setUserData('ActiveCompanyResource', {})
      userStore.setUserData('CompaniesResources', [])
      userStore.setUserData('UserCompaniesIds', [])
      userStore.setUserData('datasource', {})
      userStore.setUserData('companies', [])
      userStore.setUserData('credentials', [])
      userStore.setUserData('id', '')
      userStore.setUserData('deviceid', '')

      this.clearCalendarCache('manual')

      // Der Query-Worker hält UserId/DeviceId/ActiveCompany und sein
      // `initialisationdone` in Modulvariablen und überlebt den Logout. Ohne
      // diesen Reset leitet er weiter für den alten User ab — und der nächste
      // Login bekäme kein initsuccess → getcache mehr.
      this.QueryWorkerSendMessage({ cmd: 'resetsession' })
      // Socket der alten Anmeldung trennen — der nächste settoken (durch den
      // Reset von `settokenSent` jetzt wieder möglich) verbindet mit den
      // Zugangsdaten des neuen Users neu.
      this.ServerWorkerSendMessage({ cmd: 'userlogout' })
    },
    /**
     * Gehört eine ACL-Ableitung des Query-Workers zur aktuell angemeldeten
     * Person? Der Worker hält UserId/ActiveCompany in Modulvariablen und
     * überlebt den Logout — eine verspätete oder nach dem Logout ausgelöste
     * Ableitung würde sonst die Rechte des vorherigen Users zurückschreiben.
     */
    isAclPayloadForCurrentUser(user: any): boolean {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        console.warn('[ACL] Ableitung verworfen — es ist niemand angemeldet')
        return false
      }
      const userStore = useUserStore()
      const expected = (userStore.Userdata as any)?.id || ''
      const incoming = user?.id || ''
      if (expected && incoming && expected !== incoming) {
        console.warn(`[ACL] Ableitung verworfen — Payload gehört zu ${incoming}, angemeldet ist ${expected}`)
        return false
      }
      return true
    },
    changeCompany(companyid) {
      if (!companyid || companyid === this.config.ActiveCompany) return

      const userStore = useUserStore()
      const viewSettingsStore = useViewSettingsStore()

      this.config.ActiveCompany = companyid

      // Persist as last active company so it is restored on the next login.
      // (The ActiveCompany watcher does this too — kept here so a switch is
      // persisted even if the watchers were not registered yet.)
      viewSettingsStore.setLastActiveCompany(companyid)

      // Restore saved location/room/date/calendar view for the selected company
      this.setlocationbyactivecompany()

      // Reload all tables for the new company from IndexedDB (if available).
      // sendAllTablesToClient() re-reads the local Dexie DB and re-publishes
      // every table into the store, so the new company's data is shown without
      // requiring a server round-trip when the data is already cached locally.
      this.QueryWorkerSendMessage({ cmd: 'getcache' })

      // Refresh the user's ACL rights for the newly selected company
      if (userStore.Userdata?.datasource?.id) {
        databaseworker.postMessage({
          cmd: 'refreshuseracldata',
          id: userStore.Userdata.datasource.id,
          ac: companyid
        })
      }

      // Invalidate calendar cache on company change
      this.clearCalendarCache('company_change')
    },
    /**
     * Re-derive the current user's ACL rights from IndexedDB.
     * Pass immediate=true for startup/sync-done paths where data is already in IDB —
     * no debounce needed there. The default 400ms debounce is for pushProxy-triggered
     * refreshes where the server worker writes to IDB asynchronously.
     */
    refreshUserAclRights(immediate = false) {
      const userStore = useUserStore()
      // datasource.id is only set AFTER a successful ACL derivation — fall back
      // to the login user id (set by setAuth). If neither is available, the
      // request is STILL sent: the query worker keeps its own UserId from the
      // initial getuseracldata2 and falls back to it. Never bail silently.
      const userId = (userStore.Userdata as any)?.datasource?.id || (userStore.Userdata as any)?.id || ''
      if (!userId) console.warn('[ACL] refresh without user id in store — worker will reuse its stored UserId')
      if (aclRefreshTimer) clearTimeout(aclRefreshTimer)
      if (immediate) {
        aclRefreshTimer = null
        console.log('[ACL] immediate refresh request →', userId || '(worker UserId)', this.config.ActiveCompany)
        databaseworker.postMessage({
          cmd: 'refreshuseracldata',
          id: userId,
          ac: this.config.ActiveCompany
        })
        return
      }
      aclRefreshTimer = setTimeout(() => {
        aclRefreshTimer = null
        console.log('[ACL] refresh request →', userId || '(worker UserId)', this.config.ActiveCompany)
        databaseworker.postMessage({
          cmd: 'refreshuseracldata',
          id: userId,
          ac: this.config.ActiveCompany
        })
      }, 400)
    },
    /**
     * Clear calendar cache with reason
     */
    clearCalendarCache(reason: 'company_change' | 'data_update' | 'manual') {
      console.log(`🗑️ Clearing calendar cache: ${reason} `)

      this.temp.Calendar_events_month_cache = []
      this.temp.Calendar_bookings_month_cache = []
      this.config.month_cache = {
        start: '',
        end: '',
        year: '',
        month: -1,
        companies: '',
        initialcacheupdatedone: false
      }
      this.calendarCache.status = 'idle'
      this.calendarCache.lastUpdate = null
      this.calendarCache.error = null

      // Refresh cache with current date if reason is company change
      if (reason === 'company_change' && this.config.ActiveCompany) {
        this.refreshcalendarcaches2(this.dateconfig.ActiveDate)
      }
    },
    initQueryWorker() {
      console.log('tables initQueryWorker')
      const authStore = useAuthStore();
      const userStore = useUserStore()
      const router = useRouter();
      const route = useRoute()
      var _this = this;
      databaseworker.addEventListener('message', function (e) {
        let bookings: any[] = []
        switch (e.data.cmd) {
          case "studiodb_closed":
            // Query worker released its mycrogym_studio connection — relay the ack
            // so the server worker can run Dexie.delete() without being blocked.
            serverworker.postMessage({ cmd: 'studiodb_closed' })
            break
          case "initsuccess":
            console.log('initsuccess databaseworker')
            // Trigger an immediate getcache so IDB tables are loaded into the store
            // even when the server socket is offline or the DB is being freshly populated.
            // This unblocks the loading screen on first run / after data wipe.
            if (!getcacheTriggered) {
              getcacheTriggered = true
              _this.QueryWorkerSendMessage({ cmd: 'getcache' })
              setTimeout(() => { getcacheTriggered = false }, 5000)
            }
            break
          case "loading":
            if (e.data.payload.status === 'done') {
              console.log('query.worker.js loading done')
              _this.initalworkerdatarequestdone = true
            }
            break
          case "calendarbookingsbyactiveclient":
            bookings = JSON.parse(e.data.bookings)
            for (let i = 0; i < bookings.length; i++) {
              bookings[i].date = _this.convertDateForIos(bookings[i].date)
            }
            _this.temp.Calendar_bookings_by_activeclient = Object.assign([], bookings)
            break
          case "internalemailsbyactiveclient":
            console.log('Internalemailsbyactiveclient')
            _this.temp.communication_outbox_internal_emails = JSON.parse(e.data.emails)
            break
          case "modaleventcontracts":
            console.log('modaleventcontracts')
            // Update modals store with contracts data
            const modalsStore = useModalsStore()
            modalsStore.setModalContracts(JSON.parse(e.data.contracts))
            // Also keep in temp for backwards compatibility
            _this.temp.modaleventcontracts = JSON.parse(e.data.contracts)
            break
          case "modaleventbookings":
            bookings = JSON.parse(e.data.bookings)
            for (let i = 0; i < bookings.length; i++) {
              bookings[i].date = _this.convertDateForIos(bookings[i].date)
            }
            // Update modals store with bookings data
            useModalsStore().setModalBookings(bookings)
            // Also keep in temp for backwards compatibility
            _this.temp.modaleventbookings = Object.assign([], bookings)
            break
          case "useracldata_batch":
            // All ACL fields arrive in one message — avoids 7 separate reactive updates
            {
              const ds = e.data
              const datasource = JSON.parse(ds.datasource)
              // Der Query-Worker behält UserId/ActiveCompany über den Logout
              // hinweg. Eine nach dem Logout ausgelöste Ableitung (z. B. durch
              // eine eintreffende Sync-Nachricht) lieferte sonst die Rechte des
              // abgemeldeten Users zurück — inklusive rightsloaded = true.
              if (!_this.isAclPayloadForCurrentUser(datasource)) break
              userStore.setUserData('datasource',              datasource)
              userStore.setUserData('UserCompaniesIds',        JSON.parse(ds.UserCompaniesIds))
              userStore.setUserData('CompaniesResources',      JSON.parse(ds.CompaniesResources))
              userStore.setUserData('ActiveCompanyResource',   JSON.parse(ds.ActiveCompanyResource))
              userStore.setUserData('ActiveUserCompanyACLLevel',  JSON.parse(ds.ActiveUserCompanyACLLevel))
              userStore.setUserData('ActiveUserCompanyACLRights', JSON.parse(ds.ActiveUserCompanyACLRights))
              const wasRightsLoaded = _this.config.rightsloaded
              _this.config.isLoading = false
              _this.config.rightsloaded = true
              // Only refresh on company switch (subsequent ACL derivation), NOT on initial startup.
              // On startup, hasLoaded becomes true (via tableupdate) right before ACL completes,
              // so checking hasLoaded alone would incorrectly trigger a second sendAllTablesToClient
              // (x38 "Skipping calendar cache refresh" in the log). wasRightsLoaded guards this.
              if (_this.config.hasLoaded && wasRightsLoaded) {
                _this.refreshcalendarcaches(_this.dateconfig.ActiveDate)
              }
              if (route.fullPath === 'sign-in') router.push({ name: "Studio" })
              {
                const src = userStore.Userdata?.datasource
                _this.sendTokenToWorkerOnce(src?.at, src?.rt, src?.id, src?.di)
              }
            }
            break
          case "useracldata":
            console.log('useracldata')
            console.log(e.data.sub)
            let data = JSON.parse(e.data.data)
            // Gleiche Absicherung wie im Batch-Pfad: nach dem Logout darf keine
            // nachlaufende Ableitung mehr Rechte in den Store schreiben. Nur
            // das datasource-Teilstück trägt eine User-Id, die übrigen Nachrichten
            // hängen an derselben Anmeldung.
            if (e.data.sub !== 'error' && e.data.sub !== 'UserNotAvailableInTables'
              && !_this.isAclPayloadForCurrentUser(e.data.sub === 'datasource' ? data : null)) break
            switch (e.data.sub) {
              case 'error':
                console.error('🚫 [ACL] Rechte-Ableitung fehlgeschlagen — Menü/Routen bleiben gesperrt. Ursache:', data)
                break
              case 'UserNotAvailableInTables':
                console.log('UserNotAvailableInTables')
                serverworker.postMessage({ 'cmd': 'getusertable' })
                break
              case "datasource":
                userStore.setUserData('datasource', data)
                break
              case "UserCompaniesIds":
                userStore.setUserData('UserCompaniesIds', data)
                break
              case "CompaniesResources":
                userStore.setUserData('CompaniesResources', data)
                break
              case "ActiveCompanyResource":
                userStore.setUserData('ActiveCompanyResource', data)
                break
              case "ActiveUserCompanyACLLevel":
                userStore.setUserData('ActiveUserCompanyACLLevel', data)
                break
              case "ActiveUserCompanyACLRights":
                console.log('ActiveUserCompanyACLRights')
                console.log(data)
                {
                  const wasRightsLoaded2 = _this.config.rightsloaded
                  _this.config.isLoading = false
                  _this.config.rightsloaded = true
                  userStore.setUserData('ActiveUserCompanyACLRights', data)
                  // Only refresh on company switch (subsequent ACL), not on initial startup load.
                  if (_this.config.hasLoaded && wasRightsLoaded2) _this.refreshcalendarcaches(_this.dateconfig.ActiveDate)
                }
                if (route.fullPath === 'sign-in') router.push({ name: "Studio" });
                // On fresh DB the first getcache cycle fires before the token is available.
                // Now that ACL is complete, send settoken if it hasn't gone yet.
                // Uses sendTokenToWorkerOnce() to avoid double-send with getbrowsercache().
                {
                  const ds = userStore.Userdata?.datasource
                  _this.sendTokenToWorkerOnce(ds?.at, ds?.rt, ds?.id, ds?.di)
                }
                break
            }
            data = null
            break
          case "eventscache":
            console.log('eventscache')
            let events = JSON.parse(e.data.events)
            console.log(events.length)
            for (let i = 0; i < events.length; i++) {
              events[i].datestart = _this.convertDateForIos(events[i].datestart)
              events[i].dateend = _this.convertDateForIos(events[i].dateend)
              events[i].created = _this.convertDateForIos(events[i].created)
              events[i].updated = _this.convertDateForIos(events[i].updated)
            }
            _this.temp.Calendar_events_month_cache = Object.assign([], events)
            _this.config.eventsloaded = true
            break
          case "bookingscache":
            console.log('bookingscache')
            bookings = JSON.parse(e.data.bookings)
            console.log(bookings.length)
            for (let i = 0; i < bookings.length; i++) {
              bookings[i].date = _this.convertDateForIos(bookings[i].date)
              bookings[i].created = _this.convertDateForIos(bookings[i].created)
              bookings[i].updated = _this.convertDateForIos(bookings[i].updated)
            }
            _this.temp.Calendar_bookings_month_cache = Object.assign([], bookings)
            _this.config.bookingsloaded = true
            _this.config.isLoading = false
            _this.config.CacheisLoading = false
            break
          case "customrequest":
            console.log('customrequest response', e.data)
            // Find the matching request
            const requestIndex = _this.customrequests.findIndex(r => r.id === e.data.id)
            if (requestIndex !== -1) {
              // Parse and store the data
              const data = JSON.parse(e.data.data)
              _this.customrequests[requestIndex].data = data

              // Add to completed queue
              _this.completedqueue.push({
                id: e.data.id,
                function: e.data.function,
                year: e.data.config?.year,
                data: data
              })

              console.log(`Custom request completed: ${e.data.function} `, data.length)
            }
            break
          case "tableupdate":
            if (e.data.table === 'resources') {
              const incomingCount = JSON.parse(e.data.results).length
              console.log('tableupdate: resources —', incomingCount, 'incoming rows')
            }
            {
              // For seeding-sensitive tables, MERGE the server snapshot with any
              // records already held in memory (put there by pushProxy during seeding).
              // This prevents the getcache progressive send from wiping cards that were
              // just inserted via socketProxy but haven't been flushed to IndexedDB yet.
              const MERGE_TABLES = new Set(['dashboards', 'dashboard_cards'])
              const incoming: any[] = JSON.parse(e.data.results)
              if (MERGE_TABLES.has(e.data.table) && _this.tables[e.data.table]?.length > 0) {
                const existingById = new Map(_this.tables[e.data.table].map((r: any) => [r.id, r]))
                for (const row of incoming) existingById.set(row.id, row) // server wins for conflicts
                _this.tables[e.data.table] = Array.from(existingById.values())
              } else {
                _this.tables[e.data.table] = incoming
              }
            }
            let index = _this.config.tables.findIndex(table => table.name === e.data.table)
            _this.config.tables[index].loaded = true

            if (e.data.table === 'locations_rooms') {
              _this.setlocationbyactivecompany()
            }
            let opentables = Object.assign([], _this.config.tables.filter(table => !table.loaded || table.loaded === 'false'))
            if (opentables.length === 0) {
              // Only send settoken ONCE when all tables finish loading.
              // Uses sendTokenToWorkerOnce() — guarded by settokenSent flag —
              // to prevent double-send with getbrowsercache() and ACL completion.
              {
                const ds = userStore.Userdata?.datasource
                _this.sendTokenToWorkerOnce(ds?.at, ds?.rt, ds?.id, ds?.di)
              }

              //_this.ServerWorkerSendMessage({'cmd': 'getinitialdata', 'userid': userStore.Userdata.datasource.id})
              if (!initialWorkerDataRequested) {
                initialWorkerDataRequested = true
                _this.config.initalworkerdatarequestdone = true
                console.log('✅ Initial worker data request marked complete')

                // Re-trigger ACL data now that resources table is populated
                // On fresh install, the first getuseracldata() fired before sync completed,
                // so resources was empty and the ACL chain never completed.
                // datasource.id only exists after a SUCCESSFUL derivation — fall back to
                // the login user id, otherwise a failed first attempt deadlocks forever.
                if (!_this.config.rightsloaded) {
                  console.log('🔄 Re-triggering ACL data load (resources now available)')
                  _this.refreshUserAclRights(true)
                }
                // -> Umbauen : store.commit('updateactivesettings')
                // updateuseridbsettings('activesettingsperview')
                if (_this.config.lasturlpath === '') {
                  // -> Umbauen router.push('/dashboard')
                } else {
                  // -> Umbauen router.push(store.state.config.lasturlpath)
                }
              }
              databaseworker.postMessage({ 'cmd': 'cachereceived' })
              // DO NOT reset loaded flags - this causes dashboard cards to disappear
              // for (let i = 0; i < _this.config.tables.length; i++) {
              //   _this.config.tables[i].loaded = false
              // }

              // Only refresh calendar caches on FIRST load, not on subsequent reloads
              if (_this.config.hasLoaded === false) {
                _this.config.hasLoaded = true
                console.log('📅 Initial calendar cache refresh')
                // -> Umbauen store.dispatch('refreshcalendarcaches2', store.state.dateconfig.ActiveDate)
                _this.refreshcalendarcaches2(_this.dateconfig.ActiveDate)
              } else {
                console.log('⏭️ Skipping calendar cache refresh - already loaded')
              }
            }
            break
        }
      }, false)
    },
    QueryWorkerSendMessage(query: Record<string, any>) {
      databaseworker.postMessage(query)
    },
    initServerWorker() {
      const authStore = useAuthStore();
      const router = useRouter();
      var _this = this;
      serverworker.addEventListener('message', function (e) {
        switch (e.data.cmd) {
          case "initsuccess":
            console.log('initsuccess serverworker')
            break
          case "pushProxy":
            let data = e.data.data

            switch (data.cmd) {
              case "new":
                {
                  // Guard: table may not be initialised in the local store
                  // (e.g. clients_mapping only arrives via server push)
                  if (!_this.tables[data.table]) {
                    _this.tables[data.table] = []
                  }
                  // Upsert: if an optimistic copy already exists (same id), replace it
                  // with the server-confirmed row; otherwise push as new.
                  const existingTableIdx = _this.tables[data.table].findIndex((item: any) => item.id === data.result.id)
                  if (existingTableIdx !== -1) {
                    _this.tables[data.table].splice(existingTableIdx, 1, data.result)
                  } else {
                    _this.tables[data.table].push(data.result)
                  }
                  // Also update calendar caches so the view reflects immediately
                  if (data.table === 'calendar_events') {
                    const ev = Object.assign({}, data.result)
                    ev.datestart = _this.convertDateForIos(ev.datestart)
                    ev.dateend = _this.convertDateForIos(ev.dateend)
                    ev.created = _this.convertDateForIos(ev.created)
                    ev.updated = _this.convertDateForIos(ev.updated)
                    const existingIdx = _this.temp.Calendar_events_month_cache.findIndex(item => item.id === ev.id)
                    if (existingIdx !== -1) {
                      _this.temp.Calendar_events_month_cache.splice(existingIdx, 1, ev)
                    } else {
                      _this.temp.Calendar_events_month_cache.push(ev)
                    }
                  }
                  if (data.table === 'calendar_bookings') {
                    const bk = Object.assign({}, data.result)
                    bk.date = _this.convertDateForIos(bk.date)
                    bk.created = _this.convertDateForIos(bk.created)
                    bk.updated = _this.convertDateForIos(bk.updated)
                    // Upsert: the creator receives its own pushservice broadcast —
                    // a plain push would duplicate the optimistic copy.
                    const existingBkIdx = _this.temp.Calendar_bookings_month_cache.findIndex(item => item.id === bk.id)
                    if (existingBkIdx !== -1) {
                      _this.temp.Calendar_bookings_month_cache.splice(existingBkIdx, 1, bk)
                    } else {
                      _this.temp.Calendar_bookings_month_cache.push(bk)
                    }
                  }
                  // ACL table changed → re-derive current user's rights
                  if (['companies_acl_level_rights', 'companies_acl_mapping', 'companies_acl_level'].includes(data.table)) {
                    console.log('[pushProxy] ACL table mutated (new) → refreshing user rights')
                    _this.refreshUserAclRights()
                  }
                  
                  if (data.result?.id) {
                    const matchIdx = _this.syncQueue.findIndex((op: any) => op.table === data.table && op.recordId === data.result.id && op.status === 'pending')
                    if (matchIdx !== -1) {
                      const updated = { ..._this.syncQueue[matchIdx], status: 'done' as const }
                      _this.syncQueue.splice(matchIdx, 1, updated)
                    }
                    // Wartende socketProxyConfirmed()-Promise einloesen
                    settleWriteConfirmation(data.table, data.result.id)
                  }
                }
                break
              case "update":
                {
                  // Guard: table may not be initialised in the local store —
                  // without it this findIndex throws and kills the rest of the
                  // message handling (the 'new' case has the same guard).
                  if (!_this.tables[data.table]) {
                    _this.tables[data.table] = []
                  }
                  let index = _this.tables[data.table].findIndex(item => item.id === data.result.id)
                  let entries = _this.tables[data.table]
                  if (index !== -1) {
                    // Merge — do NOT fully replace; partial updates must not wipe unmentioned fields
                    entries.splice(index, 1, { ...entries[index], ...data.result })
                  }
                  // Also update calendar caches so the view reflects immediately
                  if (data.table === 'calendar_events') {
                    const ev = Object.assign({}, data.result)
                    ev.datestart = _this.convertDateForIos(ev.datestart)
                    ev.dateend = _this.convertDateForIos(ev.dateend)
                    ev.created = _this.convertDateForIos(ev.created)
                    ev.updated = _this.convertDateForIos(ev.updated)
                    let cacheIdx = _this.temp.Calendar_events_month_cache.findIndex(item => item.id === data.result.id)
                    if (cacheIdx !== -1) {
                      _this.temp.Calendar_events_month_cache.splice(cacheIdx, 1, ev)
                    } else if (ev.datestart && ev.dateend) {
                      // Not in the cache: the event was moved INTO the visible
                      // window by this update (or created while this client had
                      // no cache). Without the push it only appears at the next
                      // full cache rebuild. The views filter by date themselves,
                      // so an out-of-window row here is harmless.
                      _this.temp.Calendar_events_month_cache.push(ev)
                    }
                  }
                  if (data.table === 'calendar_bookings') {
                    const bk = Object.assign({}, data.result)
                    bk.date = _this.convertDateForIos(bk.date)
                    bk.created = _this.convertDateForIos(bk.created)
                    bk.updated = _this.convertDateForIos(bk.updated)
                    let cacheIdx = _this.temp.Calendar_bookings_month_cache.findIndex(item => item.id === data.result.id)
                    if (cacheIdx !== -1) {
                      _this.temp.Calendar_bookings_month_cache.splice(cacheIdx, 1, bk)
                    } else if (bk.date) {
                      _this.temp.Calendar_bookings_month_cache.push(bk)
                    }
                  }
                  if (data.table === 'communication_outbox_internal_emails') {
                    console.log('communication_outbox_internal_emails', data.result)
                  }
                  // ACL table changed → re-derive current user's rights
                  if (['companies_acl_level_rights', 'companies_acl_mapping', 'companies_acl_level'].includes(data.table)) {
                    console.log('[pushProxy] ACL table mutated (update) → refreshing user rights')
                    _this.refreshUserAclRights()
                  }
                  
                  if (data.result?.id) {
                    const matchIdx = _this.syncQueue.findIndex((op: any) => op.table === data.table && op.recordId === data.result.id && op.status === 'pending')
                    if (matchIdx !== -1) {
                      const updated = { ..._this.syncQueue[matchIdx], status: 'done' as const }
                      _this.syncQueue.splice(matchIdx, 1, updated)
                    }
                    // Wartende socketProxyConfirmed()-Promise einloesen
                    settleWriteConfirmation(data.table, data.result.id)
                  }
                }
                break
            }
            break
          case "all":
            {
              // Own const — the `data` above belongs to the pushProxy case and is
              // uninitialised (TDZ) when this case is dispatched directly.
              const allData = e.data.data
              const table = _this.tables[allData.table]
              for (let i = 0; i < allData.result.length; i++) {
                const index = table.findIndex(item => item.id === allData.result[i].id)
                if (index !== -1) {
                  // Vorhanden — splice keeps Vue reactivity intact
                  table.splice(index, 1, allData.result[i])
                } else {
                  // Neu
                  table.push(allData.result[i])
                }
              }
            }
            break
          case "socketconnection":
            console.log('🔌 socketconnection event at', new Date().toISOString())
            console.log('   Connection status:', e.data.status)
            const authStore = useAuthStore();
            authStore.loggedinbyworker(e.data.status)
            _this.config.isConnected = e.data.status
            break
          case "newloginrequired":
            // Einer von drei Wegen auf die Loginseite — ohne Spur sind sie von
            // außen nicht auseinanderzuhalten (siehe bootDiag).
            bootDiag('signin.newloginrequired', e.data?.reason)
            router.push({ name: "sign-in" });
            break
          case "newaccesstoken": {
            // Worker renewed the access token — persist it encrypted and
            // refresh the in-memory copy used by HTTP calls (datasource.at)
            if (e.data.at) {
              const userStore2 = useUserStore()
              if (userStore2.Userdata?.datasource?.at) userStore2.Userdata.datasource.at = e.data.at
              DBConfigService.checkifuserisloggedin()
                .then((row: any) => { if (row?.id) DBConfigService.insertnewuser({ ...row, at: e.data.at }) })
                .catch(() => { /* no logged-in row to update */ })
            }
            break
          }
          case "renewtokenrequired": {
            // Worker has no plaintext refresh token (encrypted at rest) —
            // renew here, where the CryptoService key lives.
            // Note: own const — `authStore` from case "socketconnection" shadows
            // the outer one at switch scope and would be in its TDZ here.
            const authStore2 = useAuthStore()
            authStore2.renewTokenForWorker().then((ok: boolean) => {
              if (ok) return
              // Vorübergehender Fehler (5xx oder gar keine Antwort): Sitzung
              // behalten. Ein Datenbank-Aussetzer von Sekunden darf nicht
              // mitten in der Arbeit auf die Loginseite werfen — der Socket
              // versucht die Erneuerung beim nächsten connect_error erneut.
              if (authStore2.renewFailedTransiently) {
                bootDiag('renewtoken.transient-session-kept')
                return
              }
              bootDiag('signin.renewtoken-failed')
              router.push({ name: "sign-in" })
            })
            break
          }
          case "syncerror": {
            // The worker has posted this since the HTTP sync was introduced, but
            // nothing listened — so a failed sync left dbloading stuck on
            // 'inprogress' forever: the "Syncing…" modal kept spinning and the
            // user saw a hang with no error anywhere. Surface it and clear the
            // in-progress state so the modal can close.
            console.error('[sync] Sync failed for company', e.data.companyid, '—', e.data.error)
            // Read BEFORE endFullSync() clears it: a Full Sync that dies only
            // took the overlay down with it, so the user was left looking at a
            // half-empty app with no idea that anything went wrong.
            const wasFullSync = _this.fullsyncrunning
            _this.endFullSync()
            if (wasFullSync) {
              import('sweetalert2/dist/sweetalert2.js').then(({ default: Swal }) => {
                Swal.fire({
                  title: 'Full Sync fehlgeschlagen',
                  text: e.data.error || 'Die Synchronisierung wurde abgebrochen.',
                  icon: 'error',
                  buttonsStyling: false,
                  confirmButtonText: 'Verstanden',
                  heightAuto: false,
                  customClass: { confirmButton: 'btn fw-semobold btn-light-primary' }
                })
              }).catch(() => { /* dialog is best-effort */ })
            }
            {
              const modalsStore = useModalsStore()
              if (modalsStore.isSchemaHealSyncing()) {
                modalsStore.failSchemaHeal(e.data.error || 'Synchronisierung fehlgeschlagen', 'sync')
              }
            }
            _this.pwa.dbloading = {
              ...(_this.pwa.dbloading as any),
              status: 'error',
              companyid: e.data.companyid,
              lastaction: e.data.error || 'Sync fehlgeschlagen'
            }
            import('@/core/services/ErrorLogService').then(m =>
              m.default.report(`Sync failed: ${e.data.error}`, {
                component: 'server.worker',
                context: { companyid: e.data.companyid }
              })
            ).catch(() => { /* reporting is best-effort */ })
            break
          }
          case "loading":
            _this.pwa.dbloading = e.data.payload
            {
              // Feed the Schema-Update modal while its resync step is running —
              // neither loading overlay covers this case (init-mode sync with
              // data already present), so this is the only progress the user sees.
              const modalsStore = useModalsStore()
              if (modalsStore.isSchemaHealSyncing()) {
                if (e.data.payload.status === 'done') modalsStore.finishSchemaHeal()
                else modalsStore.setSchemaHealProgress(e.data.payload)
              }
            }
            if (e.data.payload.status === 'done') {
              console.log('server.worker.js loading done')
              _this.endFullSync()
              // ── Always reset completion flags before setting initialimportfromserver ──
              // The dashboard-seeding watch fires the moment initialimportfromserver flips to true.
              // We must ensure isReadyToSeed()=false at that moment so seeding waits for the
              // second getcache (which loads server-synced IDB data into the store).
              // Bug: the previous !getcacheTriggered guard skipped this reset when the server sync
              // finished within the 5s initsuccess-getcache window — leaving initalworkerdatarequestdone=true
              // from the first (empty-IDB) getcache, causing premature seeding with empty store.
              for (let i = 0; i < _this.config.tables.length; i++) {
                _this.config.tables[i].loaded = false
              }
              initialWorkerDataRequested = false
              _this.config.initalworkerdatarequestdone = false
              _this.initialimportfromserver = true  // watch fires here; isReadyToSeed()=false ✅
              // NOTE: ii=true is persisted by the server worker itself, for exactly
              // the companies whose sync actually succeeded (its `final` list).
              // markAllCompaniesInitialized() used to run here and flipped ii=true
              // for EVERY non-archived company — including ones that were skipped or
              // whose sync failed. Those then did an incremental sync against data
              // they never received, which can never fill the gap again.
              // Do not reintroduce it: the worker owns this flag.
              // The first ACL derivation may have run against a still-empty DB
              // (e.g. during the initial/healing full sync). Now that all data
              // is in IndexedDB, derive the rights again immediately — no debounce
              // needed, data is already written by the sync.
              if (!_this.config.rightsloaded) {
                console.log('🔄 Sync done — retrying ACL rights derivation')
                _this.refreshUserAclRights(true)
              }
              // Always send a fresh getcache so the store reflects server-synced IDB data.
              // The initsuccess getcache may have already completed against an empty IDB;
              // we unconditionally override it here so server-written rows are loaded.
              getcacheTriggered = true
              console.log('🔄 Triggering getcache to load tables into store')
              _this.QueryWorkerSendMessage({ cmd: 'getcache' })
              setTimeout(() => { getcacheTriggered = false }, 5000)
              // ── Monats-Cache nach dem Sync neu aufbauen ────────────────────
              // Alle Kalenderansichten lesen aus temp.Calendar_events_month_cache,
              // und der wurde bisher GENAU EINMAL gefüllt: im getcache-Handler,
              // wenn `hasLoaded === false` ist. Beim Reload mit warmer IndexedDB
              // gewinnt dieser erste getcache-Zyklus das Rennen gegen den Sync —
              // der Cache entsteht also aus dem Stand VOR dem Sync, `hasLoaded`
              // steht danach auf true, und der getcache oben protokolliert nur
              // noch „Skipping calendar cache refresh". Die vom Incremental-Sync
              // frisch geschriebene Zeile (z. B. eine von einem anderen Nutzer
              // geänderte Startzeit) liegt dann korrekt in IndexedDB, erreicht
              // aber nie die Ansicht.
              // Der Watchdog in Calendar_Manager.vue fängt das nicht ab: er
              // greift nur bei LEEREM Cache, ein veralteter Cache ist nicht leer.
              // Deshalb hier explizit neu anfordern — genau einmal pro
              // Sync-Zyklus ('done' kommt aus datacheck(), nicht je Company).
              if (_this.config.hasLoaded && _this.config.ActiveCompany) {
                console.log('📅 Sync fertig — Kalender-Monats-Cache neu aufbauen')
                _this.refreshcalendarcaches2(_this.dateconfig.ActiveDate)
              }
            }
            // getuserdata fehlt
            // _this.QueryWorkerSendMessage({'cmd': 'getuseracldata2', 'id': data.id, 'di': data.di, 'ac': data.ac})
            // companies = null
            break
          case "setActiveCompany":
            // Only apply on first load (ActiveCompany not yet set) AND only
            // after the boot restore ran — otherwise this races the restore of
            // the persisted last active company and wins by accident.
            // Subsequent sync cycles also call checkActiveCompanies() which
            // sends this message with activeCompanies[0] — that must NOT
            // override a company the user manually switched to.
            if (!_this.config.ActiveCompany && activeCompanyBootstrapped) {
              console.log('Setting ActiveCompany from server worker (initial):', e.data.companyId)
              _this.config.ActiveCompany = e.data.companyId
            }
            break
          case "gmail_oauth_result":
            // Worker saved the Gmail token to IndexedDB — notify any listening components
            console.log('[Store] gmail_oauth_result:', e.data.ok, e.data.email)
            window.dispatchEvent(new CustomEvent('gmail_oauth_result', { detail: e.data }))
            break
          case "teamplanner_send_result":
            console.log('[Store] teamplanner_send_result:', e.data.ok)
            window.dispatchEvent(new CustomEvent('teamplanner_send_result', { detail: e.data }))
            break
          case "catalog_changed":
            // mgmgnt changed a template/catalog row — the Vorlagen-Katalog page
            // re-checks for pending updates if it is currently open.
            window.dispatchEvent(new CustomEvent('catalog_changed', { detail: e.data }))
            break
          case "device_status":
            // Live-Status eines Workout-Tablets (device:status vom Kiosk-Socket)
            // — Konsument: Geräte-Widget (WidgetsDevices.vue).
            window.dispatchEvent(new CustomEvent('device_status', { detail: e.data }))
            break
          case "batchGeocodeProgress":
            window.dispatchEvent(new CustomEvent('batchGeocodeProgress', { detail: e.data }))
            break
          case "batchGeocodeComplete":
            window.dispatchEvent(new CustomEvent('batchGeocodeComplete', { detail: e.data }))
            break
          case "request_studiodb_close":
            // Server worker is about to delete mycrogym_studio for a schema heal.
            // The query worker holds its own connection, which would block the
            // delete — tell it to let go. Its 'studiodb_closed' reply is relayed
            // back from the query-worker listener above.
            console.log('[SchemaGuard] Asking query worker to release mycrogym_studio')
            databaseworker.postMessage({ cmd: 'closeStudioDb' })
            break
          case "request_studiodb_reopen":
            // Full Sync / heal finished (or aborted) — the query worker closed
            // its connection with auto-open disabled and must reattach explicitly.
            databaseworker.postMessage({ cmd: 'reopenStudioDb' })
            break
          case "schema_update_available":
            console.log('[SchemaGuard] Schema update available — showing modal')
            {
              const modalsStore = useModalsStore()
              modalsStore.showSchemaUpdate({
                missingTables: e.data.missingTables || [],
                serverVersion: e.data.serverVersion || 0,
                localVersion: e.data.localVersion || 0
              })
            }
            break
          case "schema_heal_progress":
            useModalsStore().setSchemaHealStep(e.data.step, e.data.state, e.data.message)
            break
          case "schema_heal_error":
            console.error('[SchemaGuard] Heal failed at step', e.data.step, '—', e.data.message)
            useModalsStore().failSchemaHeal(e.data.message, e.data.step)
            break
          case "schema_healed":
            console.log('[SchemaGuard] Schema healed, added tables:', e.data.addedTables)
            break
          case "socketProxy_offline":
            // A socketProxy call was handled offline — notify components via CustomEvent
            console.warn(`[Offline] ${e.data.operation} on ${e.data.table} saved locally`)
            // Update queue item
            if (e.data.recordId) {
              const matchIdx = _this.syncQueue.findIndex((op: any) => op.table === e.data.table && op.recordId === e.data.recordId && op.status === 'pending')
              if (matchIdx !== -1) {
                const updated = { ..._this.syncQueue[matchIdx], status: 'offline' as const }
                _this.syncQueue.splice(matchIdx, 1, updated)
              }
            }
            window.dispatchEvent(new CustomEvent('socketProxy_offline', {
              detail: { table: e.data.table, operation: e.data.operation, recordId: e.data.recordId }
            }))
            break
          case "writefailed": {
            // Der Server hat den Schreibvorgang abgelehnt oder er ist in SQL
            // gestorben. Damit ist die Lage eindeutig — anders als beim
            // ausbleibenden Echo, das auch ein verlorenes Paket sein kann.
            const wf = e.data.data || {}
            const failIdx = _this.syncQueue.findIndex((op: any) => op.table === wf.table && op.recordId === wf.id && op.status === 'pending')
            if (failIdx !== -1) {
              _this.syncQueue.splice(failIdx, 1, { ..._this.syncQueue[failIdx], status: 'error' as const })
            }
            settleWriteConfirmation(
              wf.table, wf.id,
              new Error(`WRITE_FAILED (${wf.reason}): ${wf.cmd} ${wf.table} ${wf.id}${wf.detail ? ' — ' + wf.detail : ''}`),
            )
            break
          }
          case "socketProxy_rejected":
            // Write mit requireServer bei geschlossenem Socket: der Worker hat
            // NICHTS lokal gespeichert. Die wartende Promise muss fallen, sonst
            // liefe der Aufrufer in den Timeout und hielte den Vorgang so lange
            // für unterwegs.
            console.error('[socketProxy] Verworfen (Socket zu):', e.data.operation, e.data.table, e.data.recordId)
            if (e.data.recordId) {
              const rejIdx = _this.syncQueue.findIndex((op: any) => op.table === e.data.table && op.recordId === e.data.recordId && op.status === 'pending')
              if (rejIdx !== -1) {
                _this.syncQueue.splice(rejIdx, 1, { ..._this.syncQueue[rejIdx], status: 'error' as const })
              }
            }
            settleWriteConfirmation(
              e.data.table, e.data.recordId,
              new Error(`SOCKET_OFFLINE: ${e.data.table} ${e.data.recordId} wurde nicht geschrieben`),
            )
            break
          case "socketProxy_flushed":
            // Offline gepufferte Writes wurden beim Reconnect nachgesendet —
            // Queue-Einträge zurück auf 'pending', Komponenten benachrichtigen
            for (const rid of (e.data.recordIds || [])) {
              const flushedIdx = _this.syncQueue.findIndex((op: any) => op.recordId === rid && op.status === 'offline')
              if (flushedIdx !== -1) {
                _this.syncQueue.splice(flushedIdx, 1, { ..._this.syncQueue[flushedIdx], status: 'pending' })
              }
            }
            window.dispatchEvent(new CustomEvent('socketProxy_flushed', {
              detail: { recordIds: e.data.recordIds || [] }
            }))
            break
          case 'socketEmitWithAck_result': {
            // Resolve the Promise waiting in socketEmitWithAck()
            const resolve = ackCallbacks.get(e.data.callbackId)
            if (resolve) {
              ackCallbacks.delete(e.data.callbackId)
              resolve(e.data.result)
            }
            break
          }
        }
      }, false)

      // Listen for user confirmation from the Schema Update modal
      window.addEventListener('schema_heal_confirmed', () => {
        console.log('[SchemaGuard] User confirmed — sending heal command to worker')
        serverworker.postMessage({ cmd: 'schema_heal_confirmed' })
      })
    },
    /**
     * Start the user-triggered Full Sync and show the loading overlay.
     *
     * The old `dbloading` payload is dropped first: it still says status 'done'
     * from the last sync, and the overlay would show a finished progress bar
     * until the worker's first message arrives.
     *
     * The flag is cleared by the worker — 'loading' with status 'done' or
     * 'syncerror' (see endFullSync). The 5-minute timer only covers the case
     * where neither ever arrives; a full sync of a large company legitimately
     * takes minutes, so it must not be tighter.
     */
    beginFullSync() {
      this.pwa.dbloading = {}
      this.fullsyncrunning = true
      if (fullSyncWatchdog) clearTimeout(fullSyncWatchdog)
      fullSyncWatchdog = setTimeout(() => {
        console.warn('[fullSync] Watchdog: no completion message within 5 minutes — hiding the overlay')
        fullSyncWatchdog = null
        this.fullsyncrunning = false
      }, 300000)
      this.ServerWorkerSendMessage({ cmd: 'serverrefresh' })
    },
    endFullSync() {
      if (fullSyncWatchdog) {
        clearTimeout(fullSyncWatchdog)
        fullSyncWatchdog = null
      }
      this.fullsyncrunning = false
    },
    ServerWorkerSendMessage(query: Record<string, any>) {
      try {
        serverworker.postMessage(query)
      } catch (err) {
        // Vue reactive proxies are not structured-cloneable (DataCloneError) —
        // retry with a plain JSON copy instead of killing the caller.
        console.warn('[ServerWorkerSendMessage] postMessage failed, retrying with JSON copy:', err)
        serverworker.postMessage(JSON.parse(JSON.stringify(query)))
      }
    },
    /**
     * Schreibt über den Socket. Fire-and-forget: der Rückgabewert ist kein
     * Promise, ein `await` davor wartet auf nichts.
     *
     * `requireServer` schaltet den Offline-Auffang ab — der Worker legt den
     * Datensatz dann WEDER in IndexedDB noch in die Outbox, sondern meldet den
     * Fehlschlag zurück. Für Vorgänge, die es ohne Serverzeile gar nicht geben
     * darf (Formularprozess: die Zeile ist die Grundlage eines unterschriebenen
     * Dokuments). Wer auf die Bestätigung warten will, nimmt
     * socketProxyConfirmed().
     */
    socketProxy(data, opts: { requireServer?: boolean } = {}) {
      // Use toRaw to strip Vue reactive proxies efficiently (avoids JSON roundtrip)
      const plainData = JSON.parse(JSON.stringify(toRaw(data)))
      
      const recordId = plainData.data?.id
      if (recordId && (plainData.cmd === 'new' || plainData.cmd === 'update')) {
        const opId = crypto.randomUUID()
        const label = `${plainData.cmd === 'new' ? 'Erstellen' : 'Aktualisieren'}: ${plainData.table}`
        this.syncQueue.unshift({
          id: opId,
          cmd: plainData.cmd,
          table: plainData.table,
          recordId: recordId,
          timestamp: new Date(),
          status: 'pending',
          label
        })
        if (this.syncQueue.length > 100) this.syncQueue.pop()
      }

      const entry = {
        cmd: 'socketProxy',
        function: 'pullservice',
        dataset: plainData,
        companyid: this.config.ActiveCompany,
        requireServer: opts.requireServer === true,
      }
      serverworker.postMessage(entry)
    },
    /**
     * Beweist eine funktionierende Leitung zum Server — per Rundlauf, nicht per
     * Statusflag.
     *
     * `config.isConnected` und `socket.connected` sind auf iOS beide nicht
     * belastbar: Ein von Safari eingefrorener Socket meldet weiter `connected:
     * true` (Zombie). Ein Write geht dann in den Sendepuffer der Engine, kommt
     * nie an — und weil der Client sich für verbunden hält, greift auch der
     * Offline-Auffang im Worker nicht. Der Datensatz steht danach nirgends:
     * nicht auf dem Server, nicht in der IndexedDB.
     *
     * Der Rundlauf deckt zusätzlich den Fall ab, dass der Server-Worker gar nicht
     * mehr lebt (iOS beendet Worker unter Speicherdruck). `postMessage` in einen
     * beendeten Worker wirft nicht — es passiert schlicht nichts. Auch dann
     * bleibt die Antwort aus.
     *
     * Wirft bei geschlossenem Socket ('SOCKET_OFFLINE') und wenn niemand
     * innerhalb der Frist antwortet ('SERVER_UNREACHABLE').
     */
    async pingServer(timeoutMs = 6_000): Promise<void> {
      let result: any
      try {
        result = await this.socketEmitWithAck('alive', {}, timeoutMs)
      } catch {
        throw new Error('SERVER_UNREACHABLE: keine Antwort vom Server innerhalb von '
          + Math.round(timeoutMs / 1000) + ' s')
      }
      if (!result?.ok) {
        throw new Error(`SOCKET_OFFLINE: ${result?.error || 'keine Verbindung zum Server'}`)
      }
    },
    /**
     * Wie socketProxy(), aber das Promise löst erst auf, wenn der Server die
     * geschriebene Zeile per pushProxy zurückgespiegelt hat — der Server sendet
     * dieses Echo erst nach erfolgreichem INSERT/UPDATE.
     *
     * Es wirft, statt still lokal zu speichern:
     *   - Socket zu                → 'SOCKET_OFFLINE'     (nichts wurde geschrieben)
     *   - Server lehnt ab / SQL-Fehler → 'WRITE_FAILED'   (nichts wurde geschrieben,
     *     Grund benannt: DB_ERROR, NO_ROW, FOREIGN_ROW, OUT_OF_SCOPE, UNKNOWN_TABLE …)
     *   - kein Echo in `timeoutMs` → 'WRITE_UNCONFIRMED'
     *
     * Nur der letzte Fall ist unklar — er bleibt für das verlorene Paket und den
     * Server, der gar nicht erst antwortet. Alles, was der Server selbst bemerkt,
     * kommt seit dem 16.09.2026 als 'writefailed' zurück (reportWriteFailed() in
     * studio/server/server.js); vorher endete es ausschliesslich im Serverlog.
     *
     * Bei `cmd: 'update'` belegt das Echo, dass der Server das Statement
     * ausgeführt hat. Traf es keine Zeile, kommt vorher ein 'writefailed' mit
     * 'NO_ROW' — für MySQL ist das Erfolg, für den Aufrufer nicht.
     */
    socketProxyConfirmed(data, timeoutMs = 20_000): Promise<void> {
      const table = (data as any)?.table
      const id    = (data as any)?.data?.id
      if (!table || !id) {
        return Promise.reject(new Error('socketProxyConfirmed: table und data.id sind Pflicht'))
      }
      const key = `${table}:${id}`
      const pending = new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
          writeConfirmations.delete(key)
          reject(new Error(`WRITE_UNCONFIRMED: ${key} — der Server hat den Schreibvorgang nicht bestätigt`))
        }, timeoutMs)
        writeConfirmations.set(key, { resolve, reject, timer })
      })
      this.socketProxy(data, { requireServer: true })
      return pending
    },
    /**
     * Emit a socket event with server acknowledgement and return a Promise.
     *
     * Uses a callbackId round-trip through the server worker:
     *   main → worker: { cmd: 'socketEmitWithAck', callbackId, event, payload }
     *   server ack → worker: callback({ ok, ... })
     *   worker → main: { cmd: 'socketEmitWithAck_result', callbackId, result }
     *   main: resolves the Promise with result
     *
     * Times out after 30 s to prevent hanging callers.
     */
    socketEmitWithAck(event: string, payload: any, timeoutMs = 30_000): Promise<any> {
      return new Promise((resolve, reject) => {
        const callbackId = crypto.randomUUID()
        const timer = setTimeout(() => {
          ackCallbacks.delete(callbackId)
          reject(new Error(`socketEmitWithAck timeout for event '${event}'`))
        }, timeoutMs)

        ackCallbacks.set(callbackId, (result: any) => {
          clearTimeout(timer)
          resolve(result)
        })

        serverworker.postMessage({ cmd: 'socketEmitWithAck', callbackId, event, payload })
      })
    },
    /**
     * Batch multiple socketProxy mutations into a single debounced flush.
     * Each call within the buffer window (default 50ms) is collected and
     * sent as individual pullservice messages in one microtask.
     * Useful for bulk operations like XLS imports.
     */
    socketProxyBatch(items: Array<{ cmd: string; table: string; data: any }>, flushDelayMs = 50) {
      proxyBatchBuffer.push(...items)
      if (proxyBatchTimer) clearTimeout(proxyBatchTimer)
      proxyBatchTimer = setTimeout(() => {
        const batch = proxyBatchBuffer.splice(0)
        console.log(`[socketProxyBatch] Flushing ${batch.length} operations`)
        for (const item of batch) {
          this.socketProxy(item)
        }
      }, flushDelayMs)
    },
    /**
     * Execute a dashboard query using the new WorkerService
     */
    async executeDashboardQuery(fn: DashboardQueryFunction, config: any): Promise<any> {
      const queryId = `${fn}_${config.year || new Date().getFullYear()}_${config.companyid || this.config.ActiveCompany} `

      // Mark as loading
      this.dashboardQueries.loading.set(queryId, true)
      this.dashboardQueries.errors.delete(queryId)

      try {
        const result = await WorkerService.dashboardQuery(fn, {
          companyid: config.companyid || this.config.ActiveCompany,
          year: config.year,
          timeperiod: config.timeperiod,
          locations: config.locations,
          monthbeforeending: config.monthbeforeending
        })

        // Store result
        this.dashboardQueries.results.set(queryId, result)
        this.dashboardQueries.loading.set(queryId, false)

        return result
      } catch (error: any) {
        console.error('Dashboard query failed:', error)
        this.dashboardQueries.errors.set(queryId, error.message || 'Query failed')
        this.dashboardQueries.loading.set(queryId, false)
        throw error
      }
    },

    /**
     * Get a dashboard query result by ID
     */
    getDashboardQueryResult(queryId: string): any {
      return this.dashboardQueries.results.get(queryId)
    },

    /**
     * Check if a dashboard query is loading
     */
    isDashboardQueryLoading(queryId: string): boolean {
      return this.dashboardQueries.loading.get(queryId) || false
    },

    /**
     * Get dashboard query error
     */
    getDashboardQueryError(queryId: string): string | undefined {
      return this.dashboardQueries.errors.get(queryId)
    },

    /**
     * Clear a dashboard query
     */
    clearDashboardQuery(queryId: string): void {
      this.dashboardQueries.results.delete(queryId)
      this.dashboardQueries.loading.delete(queryId)
      this.dashboardQueries.errors.delete(queryId)
    },

    /**
     * Fetch contracts and bookings for event modal
     * Sends message to query worker which will call filtercontractsbyservice
     */
    fetchModalContractsAndBookings(serviceid: string, companyid: string) {
      databaseworker.postMessage({
        cmd: 'getmodalcontractsandbookings',
        serviceid,
        companyid
      })
    },

    /**
     * Add a booking to an event
     * Uses socketProxy pattern to delegate to server worker
     */
    addBookingToEvent(booking: any) {
      const data = {
        cmd: 'new',
        table: 'calendar_bookings',
        data: booking
      }
      this.socketProxy(data)
    },

    /**
     * Update an existing booking
     * Uses socketProxy pattern
     */
    updateBooking(booking: any) {
      const data = {
        cmd: 'update',
        table: 'calendar_bookings',
        data: booking
      }
      this.socketProxy(data)
    },

    /**
     * Update an event
     * Uses socketProxy pattern
     */
    updateEvent(event: any) {
      const data = {
        cmd: 'update',
        table: 'calendar_events',
        data: event
      }
      this.socketProxy(data)
    },

    /**
     * Create a new calendar series (recurring event)
     */
    createSeries(series: any) {
      this.socketProxy({
        cmd: 'new',
        table: 'calendar_series',
        data: series
      })
    },

    /**
     * Update an existing calendar series
     */
    updateSeries(series: any) {
      this.socketProxy({
        cmd: 'update',
        table: 'calendar_series',
        data: series
      })
    },

    /**
     * Archive/unarchive a calendar series
     */
    archiveSeries(seriesId: string, archive: boolean) {
      const series = (this.tables as any).calendar_series?.find((s: any) => s.id === seriesId)
      if (!series) return
      this.socketProxy({
        cmd: 'update',
        table: 'calendar_series',
        data: { ...series, archive: archive ? 1 : 0, updated: new Date().toISOString() }
      })
    },

    /**
     * Extend series events rolling window — called after calendar cache refresh
     * Checks all active series where generateduntil < NOW + 3 months
     */
    extendSeriesIfNeeded() {
      const seriesList = (this.tables as any).calendar_series || []
      const threeMonthsFromNow = new Date()
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3)

      for (const series of seriesList) {
        if (series.archive === 1) continue
        if (series.seriesend) continue // bounded series already fully generated
        if (!series.generateduntil) continue
        const genUntil = new Date(series.generateduntil)
        if (genUntil < threeMonthsFromNow) {
          console.log('[extendSeriesIfNeeded] Extending series:', series.id, 'generateduntil:', series.generateduntil)
          this.socketProxy({
            cmd: 'extend',
            table: 'calendar_series',
            data: series
          })
        }
      }
    },

    /**
    * Add a  new client (quick registration)
     * Uses socketProxy pattern
     */
    addClient(clientData: any, clientMapping: any) {
      // Add to clients table
      this.socketProxy({
        cmd: 'new',
        table: 'clients',
        data: clientData
      })

      // Add to clients_mapping table
      this.socketProxy({
        cmd: 'new',
        table: 'clients_mapping',
        data: clientMapping
      })
    },

    /**
     * Initialize watchers for view settings persistence
     * Call this after auth to start auto-saving activeroom/location changes
     *
     * ⚠️ Guard: both checkifuserisLoggedin() and setAuth() call this method.
     * The module-scope `watchersInitialized` flag ensures watchers are only
     * registered once per page session — duplicate registrations would cause
     * every ActiveRoom/Location change to trigger 2× saves.
     */
    initViewSettingsWatchers() {
      if (watchersInitialized) {
        console.log('⏭️ View settings watchers already initialized — skipping duplicate registration')
        return
      }
      watchersInitialized = true

      const viewSettingsStore = useViewSettingsStore()

      // Watch ActiveCompany changes — single, complete persistence point.
      // Every writer of config.ActiveCompany (changeCompany, boot restore,
      // worker fallback) ends up here, so the last active company is ALWAYS
      // stored, no matter which path selected it.
      watch(
        () => this.config.ActiveCompany,
        (newCompany) => {
          if (!newCompany) return
          viewSettingsStore.setLastActiveCompany(newCompany)
        },
        { immediate: true }
      )

      // Watch ActiveRoom changes
      watch(
        () => this.config.ActiveRoom,
        (newRoom, oldRoom) => {
          if (this.config.ActiveCompany && newRoom !== oldRoom) {
            console.log('💾 === SAVING ActiveRoom ===')
            console.log('  Company ID:', this.config.ActiveCompany, '(type:', typeof this.config.ActiveCompany, ')')
            console.log('  New Room:', newRoom)
            console.log('  Old Room:', oldRoom)
            viewSettingsStore.updateActiveSelections(
              this.config.ActiveCompany,
              { activeroom: newRoom }
            )
            console.log('💾 === SAVE COMPLETE ===')
          }
        }
      )

      // Watch ActiveLocation changes
      watch(
        () => this.config.ActiveLocation,
        (newLocation, oldLocation) => {
          if (this.config.ActiveCompany && newLocation !== oldLocation) {
            console.log(`💾 Saving ActiveLocation: ${newLocation}`)
            viewSettingsStore.updateActiveSelections(
              this.config.ActiveCompany,
              { activelocation: newLocation }
            )
          }
        }
      )

      // Watch modal open/close — persist to viewSettings so refresh can restore
      const modalsStore = useModalsStore()
      const PERSISTABLE_MODALS = [
        'addcontract', 'editcontract', 'editevent', 'editroom',
        'editcompanyinfo', 'editstaff', 'editopeninghours', 'edittask',
        'newclient', 'editEventModal',
      ] as const

      // Watch only the open/close boolean of each persistable modal — NOT deep.
      // A { deep: true } watch on modalsStore.modals fires for every nested reactive
      // mutation (e.g. 22 resources arriving during initial sync all trigger it),
      // producing a save storm even though nothing modal-relevant changed.
      watch(
        () => PERSISTABLE_MODALS.map(name => {
          const m = (modalsStore.modals as any)[name]
          return m?.show ?? m?.isOpen ?? false
        }),
        () => {
          if (!this.config.ActiveCompany) return
          let activeModal: { name: string; data: Record<string, any> } | null = null
          for (const modalName of PERSISTABLE_MODALS) {
            const modal = (modalsStore.modals as any)[modalName]
            if (!modal) continue
            const isOpen = modal.show || modal.isOpen
            if (isOpen) {
              try {
                activeModal = { name: modalName, data: JSON.parse(JSON.stringify(modal)) }
              } catch {
                activeModal = { name: modalName, data: {} }
              }
              break
            }
          }
          viewSettingsStore.updateModalSettings(this.config.ActiveCompany, activeModal)
        }
      )

      console.log('✅ View settings watchers initialized')
    },

    /**
     * Send settoken to the server worker exactly once per page session.
     * Called from getbrowsercache() (auth.ts), tableupdate handler, and
     * useracldata/ActiveUserCompanyACLRights handler. The flag `settokenSent`
     * prevents the socket from being reconnected on every redundant call.
     *
     * If `at` is not a plain string (EncryptedBlob — decrypt failed, or the
     * CryptoService key is not yet ready), we still need the worker to boot.
     * In that case the worker cannot use the token directly, so we send a
     * minimal settoken with an empty token AND userid/deviceid so the worker
     * can call getnewaccesstoken() → renewtokenrequired → main-thread renewal.
     * This unblocks the chicken-and-egg on fresh staging environments where
     * the at field is encrypted but the worker has no plaintext copy yet.
     */
    // Die Parameter dürfen undefined sein: alle Aufrufer lesen sie über
    // `userStore.Userdata?.datasource?.…`, und genau dafür hat die Funktion
    // ihren Guard im Rumpf. Die Signatur behauptete bisher das Gegenteil.
    sendTokenToWorkerOnce(at?: string, rt?: string, userid?: string, deviceid?: string, activecompany?: string) {
      if (settokenSent) return
      // Require at least userid + deviceid to be strings so the worker
      // can look up the device and issue a renewtokenrequired request.
      if (!userid || !deviceid) return
      settokenSent = true
      const tokenStr = typeof at === 'string' && at ? at : ''
      const rtStr    = typeof rt  === 'string' && rt  ? rt  : ''
      this.ServerWorkerSendMessage({
        cmd: 'settoken',
        token: tokenStr,
        rt: rtStr,
        userid,
        deviceid,
        refresh: false,
        activecompany: activecompany || this.config.ActiveCompany
      })
    },

    /**
     * Restore view settings for current company
     * Call this after company is set and tables are loaded
     */
    restoreViewSettings() {
      if (!this.config.ActiveCompany) return

      const viewSettingsStore = useViewSettingsStore()
      const settings = viewSettingsStore.getCompanySettings(this.config.ActiveCompany)

      console.log('🔄 Restoring view settings:', settings)

      if (settings.activelocation) {
        this.config.ActiveLocation = settings.activelocation
      }
      if (settings.activeroom) {
        this.config.ActiveRoom = settings.activeroom
      }

      // ── Restore calendar toolbar settings ──────────────────────────────
      if (settings.calendarcellheight) {
        this.dateconfig.cellheight = Number(settings.calendarcellheight)
      }
      if (settings.calendardaystoshow) {
        this.dateconfig.daystoshow = Number(settings.calendardaystoshow)
      }
      if (settings.calendartimestepsinminutes) {
        this.dateconfig.ViewedTimeStepsInMinutes = Number(settings.calendartimestepsinminutes)
      }
      if (typeof settings.calendarshowresources === 'boolean') {
        this.dateconfig.ShowResources = settings.calendarshowresources
      }
      if (typeof settings.calendarshowresourcesoverview === 'boolean') {
        this.dateconfig.ShowResourcesOverview = settings.calendarshowresourcesoverview
      }
      if (typeof settings.calendarshowarchivedtrainings === 'boolean') {
        this.dateconfig.showarchivedtrainings = settings.calendarshowarchivedtrainings
      }
      if (typeof settings.calendarshowtodos === 'boolean') {
        this.dateconfig.showtodos = settings.calendarshowtodos
      }
      // Restore selected days (Anzeige Tage)
      if (settings.calendarselecteddays && settings.calendarselecteddays.length > 0) {
        const dayLabels: Record<number, string> = {
          0: 'Sonntag', 1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch',
          4: 'Donnerstag', 5: 'Freitag', 6: 'Samstag'
        }
        this.dateconfig.viewdays = settings.calendarselecteddays.map((v: any) => {
          const num = Number(v)
          return { value: num, day: dayLabels[num] }
        })
      }

      // Restore open modal — re-open the modal that was active before the refresh
      if (settings.activeModal) {
        const modalsStore = useModalsStore()
        const { name, data } = settings.activeModal
        console.log('🔄 Restoring active modal:', name, data)

        switch (name) {
          case 'addcontract':
            modalsStore.modaladdcontract({ show: true, data: data.data, type: data.type })
            break
          case 'editcontract':
            modalsStore.modaleditcontract({ show: true, data: data.data, bookings: data.bookings ?? [] })
            break
          case 'editevent':
            modalsStore.modaleditevent({ show: true, dataobject: data.data, times: data.times })
            break
          case 'editroom':
            modalsStore.modaleditroom({ show: true, dataobject: data.data })
            break
          case 'editcompanyinfo':
            modalsStore.modalEditCompanyInfo({ show: true, type: data.type, dataobject: data.dataobject })
            break
          case 'editstaff':
            modalsStore.modalEditStaff({ show: true, dataobject: data.dataobject })
            break
          case 'editopeninghours':
            modalsStore.modaleditopeninghours({ show: true, dataobject: data.data })
            break
          case 'edittask':
            modalsStore.modaledittask({ show: true, modus: data.modus, id: data.id })
            break
          case 'newclient':
            if (data.show) modalsStore.modalnewclient()
            break
          case 'editEventModal':
            if (data.isOpen && data.eventData) modalsStore.openEditEventModal(data.eventData)
            break
        }
      }
    }
  }
})
